/**
 * PlanRevealModal — the deferred paywall (ROADMAP Epic 0.4).
 *
 * Price is revealed ONLY here, at the conversion CTAs in the risk-assessment
 * flow (see docs/conversion-moment-mockup.html and PRODUCT.md "Where pricing
 * surfaces") — never on the landing page. Confirming starts Stripe checkout.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { baseBrandColors, typography } from '../../common/styles/universalTheme';
import { createCheckout, PaidPlan, upgradeSubscription } from '../../services/billingService';
import { refreshEntitlements } from '../../services/entitlementService';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import analyticsService from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';

interface PlanContent {
  eyebrow: string;
  title: string;
  price: string;
  /** List price, whole dollars a month — the reader's own figure is derived. */
  monthly: number;
  includes?: string;
  features: string[];
  proof: string;
  cta: string;
  fine: string;
}

const PLAN_CONTENT: Record<PaidPlan, PlanContent> = {
  signals: {
    eyebrow: 'Daily protection',
    title: "We watch your portfolio every day so you don't have to.",
    price: '$100',
    monthly: 100,
    // Only what runs today (ROADMAP 0.R R3): the summary and the risk report
    // are fortnightly, and how holdings move together (correlation clustering)
    // is not built — PRODUCT.md says not to pitch it.
    features: [
      'Your portfolio\'s risk, re-checked every morning',
      'Alerts the moment something shifts',
      'A summary every two weeks of what moved, and why',
      'A full risk report every two weeks',
    ],
    proof: 'Runs before the market opens, every trading day.',
    cta: 'Start daily protection',
    fine: 'Cancel anytime. You keep the free tools either way.',
  },
  strategist: {
    eyebrow: 'Rebalance simulation',
    title: 'See how each way to rebalance survives the next crash.',
    price: '$200',
    monthly: 200,
    includes: 'Includes everything in daily protection.',
    features: [
      'Every way to rebalance, side by side — each stress-tested against real crashes',
      'Save a mix you like and re-check it on today\'s data',
      'Two portfolios watched for drift, with a nudge when one wanders',
      'Expert mode, and 1,000 actions a day',
    ],
    proof: 'Built from real historical crashes — not guesses.',
    cta: 'Simulate strategies',
    fine: "A simulation and comparison tool — it never places a trade, and it isn't financial advice.",
  },
};

/**
 * PlanDiagram — a small, self-contained "what you're buying" picture drawn
 * between the promise and the price. No external images, palette-only
 * (monochrome + green/red). The shapes are illustrative of the *mechanic*,
 * not real figures — no numbers are shown, so nothing is fabricated.
 */
const PlanDiagram: React.FC<{ plan: PaidPlan }> = ({ plan }) => {
  const theme = useTheme();

  const frame = (children: React.ReactNode, caption: string) => (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.default',
        p: 2,
        mt: 2,
      }}
    >
      {children}
      <Typography sx={{ fontSize: typography.fontSize.xs, color: 'text.secondary', mt: 1.5, textAlign: 'center', lineHeight: 1.5 }}>
        {caption}
      </Typography>
    </Box>
  );

  if (plan === 'signals') {
    // A week of mornings, each one checked — with the one day that catches a shift.
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const alertIdx = 3;
    return frame(
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {days.map((d, i) => {
          const isAlert = i === alertIdx;
          return (
            <Box key={d} sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  height: 34,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isAlert ? 'error.main' : 'divider',
                  bgcolor: isAlert ? alpha(theme.palette.error.main, 0.12) : 'background.paper',
                  color: isAlert ? 'error.main' : 'success.main',
                  fontWeight: typography.fontWeight.bold,
                  fontSize: typography.fontSize.md,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isAlert ? '↓' : '✓'}
              </Box>
              <Typography sx={{ fontSize: typography.fontSize.xs, color: 'text.disabled', mt: 0.5 }}>{d}</Typography>
            </Box>
          );
        })}
      </Box>,
      "This is the kind of day we'd catch — before you would.",
    );
  }

  // strategist — the ways to rebalance, ranked by how they hold up. NOT a
  // count: the gateway runs `STRATEGIES` (three today) and the candidate set
  // is meant to grow, so a number here becomes a promise we have to keep in
  // two places. Naming five while running three is the [[BUGS #94]] shape —
  // offering the reader something the data does not answer.
  // Bar lengths show ordering only; no percentages, no invented numbers.
  const bars = [
    { w: '100%', color: theme.palette.success.main, label: 'kept the most' },
    { w: '80%', color: alpha(theme.palette.text.primary, 0.55), label: '' },
    { w: '62%', color: alpha(theme.palette.text.primary, 0.42), label: '' },
    { w: '44%', color: alpha(theme.palette.text.primary, 0.3), label: '' },
    { w: '28%', color: theme.palette.error.main, label: 'fell the hardest' },
  ];
  return frame(
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
      {bars.map((b, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, height: 12, borderRadius: 0.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ width: b.w, height: '100%', bgcolor: b.color }} />
          </Box>
          <Typography sx={{ fontSize: typography.fontSize.xs, color: 'text.secondary', width: 92, flexShrink: 0, lineHeight: 1.3 }}>
            {b.label}
          </Typography>
        </Box>
      ))}
    </Box>,
    "How each one would've held up in a real crash — ranked, not guessed.",
  );
};

interface PlanRevealModalProps {
  open: boolean;
  plan: PaidPlan | null;
  onClose: () => void;
  /** Why the reader is seeing this — the refusal's own sentence, when a paywall raised it (ROADMAP 0.R R2). */
  reason?: string;
}

const PlanRevealModal: React.FC<PlanRevealModalProps> = ({ open, plan, onClose, reason }) => {
  const { user, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!plan) return null;
  const content = PLAN_CONTENT[plan];
  const planName = plan === 'signals' ? 'Signals' : 'Strategist';

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      // Anonymous visitor at peak intent: sign in FIRST (their just-run
      // analysis is preserved in session), then continue straight to
      // checkout — instead of a bare 401 dead-end (BUG #13). We check the
      // live Firebase user, not the React prop, so it's correct mid-call.
      if (!auth.currentUser) {
        // Popup on a desktop; on a phone at the prod origin a redirect, which
        // navigates away — say nothing then, the page is leaving.
        if ((await signInWithGoogle()) === 'redirecting') return;
        if (!auth.currentUser) {
          // Popup dismissed without signing in — not an error, just stop.
          setError('Sign in to continue — your analysis is saved.');
          setLoading(false);
          return;
        }
      }
      analyticsService.trackCheckoutStarted(plan);
      // A paying reader CHANGES plan; only a new customer checks out
      // ([[BUGS #497]], the same rule as /plans — [[BUGS #200]]): a second
      // checkout is refused as ALREADY_SUBSCRIBED, which left the reader most
      // likely to pay more stopped at the button.
      const current = useSubscriptionStore.getState().plan;
      if (current === plan) {
        setDone(`You're already on ${planName}.`);
        setLoading(false);
        return;
      }
      if (current && current !== 'free') {
        try {
          await upgradeSubscription();
          await refreshEntitlements();
          setDone(`You're on ${planName} now — the change shows on your next invoice.`);
          setLoading(false);
          return;
        } catch (e) {
          // No subscription to change (a complimentary plan): check out instead.
          if ((e as { code?: string })?.code !== 'NO_SUBSCRIPTION') throw e;
        }
      }
      await createCheckout(plan); // redirects to Stripe on success
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    setDone(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 } }}
    >
      <DialogContent sx={{ p: 3.5 }}>
        {reason && (
          <Typography role="note" sx={{ fontSize: typography.fontSize.sm, color: 'text.primary', mb: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            {reason}
          </Typography>
        )}
        <Typography sx={{ fontSize: typography.fontSize.xs, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'text.disabled' }}>
          {content.eyebrow}
        </Typography>
        <Typography sx={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, mt: 1.25, lineHeight: 1.3, color: 'text.primary' }}>
          {content.title}
        </Typography>

        <PlanDiagram plan={plan} />

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 2.5 }}>
          <Typography sx={{ fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.bold, fontFamily: typography.fontFamily.mono, color: 'text.primary' }}>
            {content.price}
          </Typography>
          <Typography sx={{ fontSize: typography.fontSize.base, color: 'text.secondary' }}>/ month</Typography>
        </Box>
        {content.includes && (
          <Typography sx={{ fontSize: typography.fontSize.sm, color: 'text.secondary', mt: 0.5 }}>
            {content.includes}
          </Typography>
        )}

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: '18px 0 22px', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {content.features.map((f) => (
            <Box component="li" key={f} sx={{ display: 'flex', gap: 1.25, fontSize: typography.fontSize.base, lineHeight: 1.5, color: 'text.primary' }}>
              <Box component="span" sx={{ color: 'success.main', fontWeight: typography.fontWeight.bold, flexShrink: 0 }}>✓</Box>
              <span>{f}</span>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box sx={{ color: 'success.main', fontWeight: typography.fontWeight.bold, flexShrink: 0 }}>✓</Box>
          <Typography sx={{ fontSize: typography.fontSize.sm, color: 'text.secondary' }}>{content.proof}</Typography>
        </Box>

        {done && (
          <Typography role="status" sx={{ fontSize: typography.fontSize.sm, color: 'success.main', mb: 1.5 }}>{done}</Typography>
        )}
        {error && (
          <Typography sx={{ fontSize: typography.fontSize.sm, color: 'error.main', mb: 1.5 }}>{error}</Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleStart}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            bgcolor: baseBrandColors.buttons.green.main,
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: typography.fontWeight.semibold,
            py: 1.25,
            '&:hover': { bgcolor: baseBrandColors.buttons.green.hover },
          }}
        >
          {loading ? 'Starting…' : content.cta}
        </Button>
        {!user && (
          <Typography sx={{ textAlign: 'center', fontSize: typography.fontSize.xs, color: 'text.secondary', mt: 1 }}>
            You&apos;ll create a free account first — your analysis is saved.
          </Typography>
        )}
        <Button
          fullWidth
          onClick={handleClose}
          disabled={loading}
          sx={{ mt: 1, color: 'text.secondary', textTransform: 'none' }}
        >
          Not now
        </Button>

        <Typography sx={{ textAlign: 'center', fontSize: typography.fontSize.xs, color: 'text.disabled', mt: 1.5, lineHeight: 1.5 }}>
          {content.fine}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default PlanRevealModal;
